import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const LEVELS = [
  { value: 'private', label: 'Private — login required' },
  { value: 'public', label: 'Public — listed on anonymous welcome page (subject to per-job rules for jobs)' },
  { value: 'confidential', label: 'Confidential — same as private for anonymous visitors' }
];

const VisibilitySetup = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [training, setTraining] = useState('private');
  const [jobs, setJobs] = useState('private');
  const [exams, setExams] = useState('private');
  const [benches, setBenches] = useState('private');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/settings');
      const grouped = r.data?.data || {};
      const vis = grouped.visibility || [];
      const pick = (key) => {
        const row = vis.find((s) => s.key === key);
        const v = (row?.value || 'private').toLowerCase();
        return LEVELS.some((l) => l.value === v) ? v : 'private';
      };
      setTraining(pick('visibility.training'));
      setJobs(pick('visibility.jobs'));
      setExams(pick('visibility.exams'));
      setBenches(pick('visibility.benches'));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load visibility settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/settings/batch', {
        settings: [
          { key: 'visibility.training', value: training },
          { key: 'visibility.jobs', value: jobs },
          { key: 'visibility.exams', value: exams },
          { key: 'visibility.benches', value: benches }
        ]
      });
      toast.success('Visibility settings saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: embedded ? 4 : 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!embedded && (
        <>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" /> Visibility setup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
            Control what anonymous visitors can see on the public welcome page (before login). Jobs also require each job to
            be set to <strong>Public</strong> under Job visibility to appear when tenant Jobs is Public.
          </Typography>
        </>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        After enabling <strong>Public</strong>, open the site without logging in — the home page lists allowed content.
        Training and exams still require login to enroll or take attempts unless you add separate anonymous flows later.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Training</InputLabel>
                <Select value={training} label="Training" onChange={(e) => setTraining(e.target.value)}>
                  {LEVELS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Jobs</InputLabel>
                <Select value={jobs} label="Jobs" onChange={(e) => setJobs(e.target.value)}>
                  {LEVELS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Exams</InputLabel>
                <Select value={exams} label="Exams" onChange={(e) => setExams(e.target.value)}>
                  {LEVELS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Bench visibility</InputLabel>
                <Select value={benches} label="Bench visibility" onChange={(e) => setBenches(e.target.value)}>
                  {LEVELS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                When Public, anonymous visitors see benches that are marked <strong>Public</strong> on each bench (with
                candidates). Individual benches can be Private or Confidential inside the app.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />} disabled={saving} onClick={save}>
        Save
      </Button>
    </Box>
  );
};

export default VisibilitySetup;
